// The desktop Slow Burn run's side panels, as the daily game has: the day's run, its gold letters
// and the letters still standing on the left; your whole run and today's scoreboard on the right. Hidden on phones,
// where the run screen shows the last few rows itself.
import type { SlowBurnInfo, SlowBurnRun } from '@wbwl/contract'
import { scoreWord } from '@wbwl/rules'
import { Link } from 'react-router'
import { useSlowBurnBoard } from '../lib/data.ts'
import { fmt, goldByTier, shortDate } from '../lib/game.ts'
import { lettersStanding } from '../lib/slow-burn.ts'
import { Tile } from './game-ui.tsx'
import { RunWord, SlowBurnMark } from './slow-burn-ui.tsx'

export function RunPanel({ info, run, left }: { info: SlowBurnInfo; run: SlowBurnRun; left: number }) {
  const standing = lettersStanding(run.ashes)
  return (
    <aside className="side side-left">
      <div className="panel">
        <div className="double-rule" />
        <div className="eyebrow dateline"><span className="sb-dateline"><SlowBurnMark />No. {info.number}</span><span>{shortDate(info.date)}</span></div>
        <div className="rule ink" />
        <div className="h-serif side-headline">One letter burns <i className="burn-em">every</i> row.</div>
        <div className="tiers">
          {goldByTier(info.gold).map(([tier, letters]) => (
            <div className="tier" key={tier}>
              <span className="tl">+{tier}</span>
              <div className="ts">{letters.map((l) => <Tile key={l} l={l} k={`played gold${run.ashes.includes(l) ? ' dim' : ''}`} size={28} gb={false} pt={false} gold={info.gold} />)}</div>
            </div>
          ))}
        </div>
        <p className="muted small">Gold burns like any other letter. You see one row ahead; the rest print as you reach them.</p>
      </div>
      <div className="panel">
        <div className="sb-standing first">
          <span className="eyebrow">Still standing</span>
          <span className="mono">{standing.join(' ')}</span>
        </div>
        <p className="muted small">{fmt(left)} word{left === 1 ? '' : 's'} still fit. {left < 100 ? 'Choose what burns carefully.' : 'Row 25 is a perfect run.'}</p>
      </div>
    </aside>
  )
}

export function RunCardPanel({ info, run, typing, valid }: { info: SlowBurnInfo; run: SlowBurnRun; typing: string; valid: boolean }) {
  const live = typing.length === 5 && valid ? scoreWord(typing, info) : null
  const board = useSlowBurnBoard(info.number, 'today').data
  return (
    <aside className="side side-right">
      <div className="panel">
        <div className="panel-head"><b>Your run</b><span className="eyebrow">pts × mult</span></div>
        <div className="sb-card-rows">
          {run.rows.map((w, r) => {
            const s = run.scores.rows[r]
            return <div className="scr" key={r}><span className="n">{r + 1}</span><RunWord gold={info.gold} rulesVersion={info.rulesVersion} word={w} col={run.cols[r]} size={22} gap={3} /><span className="m">{s.pts}×{s.mult}</span><span className="s">{s.total}</span></div>
          })}
          <div className="scr live"><span className="n on">▸</span>{typing ? <span className="mono">{typing}</span> : <span className="empty">row {run.rows.length + 1} · typing…</span>}<span className="m">{live ? `${live.pts}×${live.mult}` : ''}</span><span className="s faint">{live ? live.total : ''}</span></div>
        </div>
        <div className="total-row"><span className="eyebrow ink">Row {run.rows.length + 1} · points</span><span className="total-num">{fmt(run.scores.points)}</span></div>
      </div>
      <div className="panel">
        <div className="panel-head"><b>Today’s scoreboard</b><Link className="eyebrow" to="/leaderboard?mode=slow-burn">see all</Link></div>
        {!board || board.entries.length === 0 ? (
          <p className="muted small">Nobody has finished a run yet. Most points wins.</p>
        ) : (
          <div className="sb-mini">
            {board.entries.slice(0, 5).map((e) => (
              <div key={e.playerId} className={e.isMe ? 'me' : ''}><span className="p">{e.position}</span><span className="ellipsis">{e.displayName}</span><span className="d">{fmt(e.points)} · row {e.depth}</span></div>
            ))}
            <div className="you"><span className="p">—</span><span>You, so far</span><span className="d">{fmt(run.scores.points)} · row {run.rows.length}</span></div>
          </div>
        )}
      </div>
      <div className="kbd-hints faint">
        <span className="kbd">A–Z</span><span>type</span><span className="kbd">⏎</span><span>play word</span><span className="kbd">⌫</span><span>delete</span><span className="kbd">?</span><span>rules</span>
      </div>
    </aside>
  )
}
